import React, { useMemo, useState } from 'react'
import {
  Badge,
  DescriptionList,
  DESCRIPTION_LIST_CLAIMS_GROUPS,
  DESCRIPTION_LIST_DEMO_ITEMS,
  DESCRIPTION_LIST_FILE_DETAILS_ITEMS,
  DESCRIPTION_LIST_PREVIEW_STATE_LABELS,
  DESCRIPTION_LIST_PREVIEW_STATES,
  type DescriptionListColumns,
  type DescriptionListPreviewState,
  type DescriptionListVariant,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './DescriptionListShowcase.css'

const DescriptionListShowcase: React.FC = () => {
  const [variant, setVariant] = useState<DescriptionListVariant>('panel')
  const [columns, setColumns] = useState<DescriptionListColumns>(3)
  const [highlightedState, setHighlightedState] =
    useState<DescriptionListPreviewState>('default')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'description-list-highlight',
            label: 'Highlighted state',
            control: (
              <select
                id="description-list-highlight"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as DescriptionListPreviewState,
                  )
                }
              >
                {DESCRIPTION_LIST_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {DESCRIPTION_LIST_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'description-list-variant',
            label: 'Variant',
            control: (
              <select
                id="description-list-variant"
                className="ui-field-control"
                value={variant}
                onChange={(event) =>
                  setVariant(event.target.value as DescriptionListVariant)
                }
              >
                <option value="panel">Panel (tinted)</option>
                <option value="plain">Plain</option>
                <option value="file-details">File details</option>
              </select>
            ),
          },
          {
            id: 'description-list-columns',
            label: 'Columns',
            control: (
              <select
                id="description-list-columns"
                className="ui-field-control"
                value={columns}
                onChange={(event) =>
                  setColumns(
                    Number(event.target.value) as DescriptionListColumns,
                  )
                }
                disabled={variant === 'file-details'}
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
              </select>
            ),
          },
        ],
      },
    ],
    [columns, highlightedState, variant],
  )

  const liveVariant: DescriptionListVariant =
    highlightedState === 'plain'
      ? 'plain'
      : highlightedState === 'file-details'
        ? 'file-details'
        : variant

  const liveItems =
    liveVariant === 'file-details'
      ? DESCRIPTION_LIST_FILE_DETAILS_ITEMS
      : DESCRIPTION_LIST_DEMO_ITEMS

  const liveColumns: DescriptionListColumns =
    liveVariant === 'file-details' ? 1 : columns

  return (
    <>
      <UiPreviewCanvas
        title="Description list"
        description="Label/value field rows — Claims Transaction info (panel) and Files → File Details (file-details)."
        stageClassName="ui-preview__stage--wide description-list-showcase__stage-wrap"
      >
        <div className={`description-list-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Property controls drive the live list when Highlighted state is
              Default. Other states show that preset. File details uses a single
              column with dashed connectors.
            </p>
            {highlightedState === 'default' ? (
              <DescriptionList
                items={liveItems}
                columns={liveColumns}
                variant={liveVariant}
              />
            ) : (
              <DescriptionList previewState={highlightedState} />
            )}
          </section>

          <section className="description-list-showcase__demo">
            <h3 className="ui-showcase-live__title">
              Claims — Transaction info
            </h3>
            <p className="ui-showcase-live__note">
              Explicit column groups matching ClaimPostingDetails (patient /
              claim / payment).
            </p>
            <DescriptionList
              groups={[
                DESCRIPTION_LIST_CLAIMS_GROUPS[0],
                [
                  ...DESCRIPTION_LIST_CLAIMS_GROUPS[1].slice(0, 1),
                  {
                    label: 'Claim status',
                    value: (
                      <Badge label="Posted" mode="tinted" color="vivid-green" />
                    ),
                  },
                  ...DESCRIPTION_LIST_CLAIMS_GROUPS[1].slice(1),
                ],
                [
                  ...DESCRIPTION_LIST_CLAIMS_GROUPS[2].slice(0, 1),
                  {
                    label: 'Status',
                    value: (
                      <Badge label="Deposited" mode="tinted" color="teal" />
                    ),
                  },
                  ...DESCRIPTION_LIST_CLAIMS_GROUPS[2].slice(1),
                ],
              ]}
              variant="panel"
            />
          </section>

          <section className="description-list-showcase__demo">
            <h3 className="ui-showcase-live__title">Files — File details</h3>
            <p className="ui-showcase-live__note">
              File details variant: gray labels, dashed connectors, single
              column — as in FileDetailsModal.
            </p>
            <DescriptionList
              items={DESCRIPTION_LIST_FILE_DETAILS_ITEMS}
              variant="file-details"
            />
          </section>

          <section className="description-list-showcase__sample">
            <h3 className="ui-showcase-live__title">Selected state</h3>
            <p className="ui-showcase-live__note">
              Shows the Highlighted state from the properties panel.
            </p>
            <div className={`description-list-showcase__row`}>
              <h4 className="description-list-showcase__label">
                {DESCRIPTION_LIST_PREVIEW_STATE_LABELS[highlightedState]}
              </h4>
              <DescriptionList previewState={highlightedState} />
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="DescriptionList"
        sections={propertySections}
      />
    </>
  )
}

export default DescriptionListShowcase
